import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import {
  ForgotPasswordRequest,
  type ForgotPasswordRequest as ForgotPasswordRequestType,
} from "@vedaunion/shared";
import { EditorialAuthShell } from "../components/auth/EditorialAuthShell";
import { AuthInput } from "../components/auth/AuthInput";
import { useForgotPassword } from "../lib/auth";

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const { mutateAsync, isPending } = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordRequestType>({
    resolver: zodResolver(ForgotPasswordRequest),
    defaultValues: { email: "" },
  });

  const onSubmit = async (values: ForgotPasswordRequestType) => {
    await mutateAsync(values).catch(() => {
      /* swallow — endpoint always returns 200, but tolerate net errors too */
    });
    setSubmitted(true);
  };

  return (
    <EditorialAuthShell
      eyebrow="Recover access"
      headline={
        <>
          A path{" "}
          <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
            back
          </span>
          .
        </>
      }
      lede={
        submitted
          ? undefined
          : "Enter your email — if we recognise it, a recovery link is on its way."
      }
      verse={{
        deva: "तमसो मा ज्योतिर्गमय",
        iast: "Tamaso mā jyotir gamaya",
        english: "From darkness, lead me to light.",
        source: "Bṛhadāraṇyaka Upaniṣad 1.3.28",
      }}
    >
      {submitted ? (
        <div>
          <p className="font-display text-[1.35rem] leading-[1.4] text-[var(--color-ink)]">
            If an account exists for that email, a recovery link is on its way.
          </p>
          <p className="text-[0.97rem] leading-[1.55] text-[var(--color-ink-soft)] mt-3 max-w-[40ch]">
            Check your inbox in a few minutes. The link is valid for one hour.
          </p>
          <div className="auth-links">
            <Link to="/login">← Back to sign in</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <AuthInput
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            {...register("email")}
            error={errors.email?.message}
          />

          <div className="auth-actions">
            <button type="submit" className="btn-primary auth-submit" disabled={isPending}>
              <span>{isPending ? "Sending…" : "Send recovery link"}</span>
              <span className="btn-arrow-icon" aria-hidden>
                →
              </span>
            </button>
          </div>

          <div className="auth-links">
            <Link to="/login">← Back to sign in</Link>
          </div>
        </form>
      )}
    </EditorialAuthShell>
  );
}
